import React, { useEffect, useRef, useState } from "react";
import { GOOGLE_API_KEY } from "src/lib/constans";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { CircularProgress } from "@mui/material";
import GradientBtn from "../WebsiteComponents/GradientBtn";

interface AddressDetails {
  emirate: string;
  city: string;
  streetAddress: string;
  country: string;
  fullAddress: string;
  address: string;
}

interface AddressSelectorProps {
  onConfirm: (address: AddressDetails) => void;
  loading?: boolean;
  className?: string;
  mapContainerClass?: string;
  btnContainerClass?: string;
  containerStyle?: React.CSSProperties;
  titleClass?: string;
  addressDetailsClass?: string;
  inputId?: string;
  confirmBtnId?: string;
  suggestionId?: string; // New prop for suggestions dropdown id
}

// Pakistan boundary coordinates (approximate)
const PAKISTAN_BOUNDS = {
  north: 37.0841,
  south: 23.6345,
  east: 77.8375,
  west: 60.8742
};

const defaultContainerStyle = {
  width: "100%",
  height: "35vh",
  borderRadius: "3px",
  // border: "1px solid #707070",
};

const initialCenter = {
  lat: 24.8607,
  lng: 67.0011,
};

const GoogleMapComponent: React.FC<AddressSelectorProps> = ({
  onConfirm,
  loading = false,
  className = "",
  mapContainerClass = "",
  btnContainerClass = "",
  containerStyle,
  titleClass = "",
  addressDetailsClass = "",
  inputId = "",
  confirmBtnId = "",
  suggestionId = "autocomplete-suggestions",
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markerPosition, setMarkerPosition] = useState(initialCenter);
  const [addressDetails, setAddressDetails] = useState<AddressDetails | null>(
    null
  );
  const [validationError, setValidationError] = useState<string>("");

  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: ["places"],
  });

  // Function to check if coordinates are within Pakistan bounds
  const isWithinPakistanBounds = (lat: number, lng: number): boolean => {
    return (
      lat >= PAKISTAN_BOUNDS.south &&
      lat <= PAKISTAN_BOUNDS.north &&
      lng >= PAKISTAN_BOUNDS.west &&
      lng <= PAKISTAN_BOUNDS.east
    );
  };

  // Function to validate address details
  const validateAddress = (address: AddressDetails): { isValid: boolean; error: string } => {
    // Check if country is Pakistan
    if (address.country && address.country.toLowerCase() !== "pakistan") {
      return {
        isValid: false,
        error: "Sirf Pakistan ki location select kar sakte hain. Please Pakistan ki koi location choose karein."
      };
    }

    // Check if we have valid address components
    if (!address.fullAddress || address.fullAddress.trim() === "") {
      return {
        isValid: false,
        error: "Invalid location selected. Please choose a valid address."
      };
    }

    // Check if we have at least city or emirate
    if (!address.city && !address.emirate) {
      return {
        isValid: false,
        error: "Please select a complete address with city or province information."
      };
    }

    return { isValid: true, error: "" };
  };
  
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          // Check if current location is within Pakistan bounds
          if (isWithinPakistanBounds(lat, lng)) {
            setMarkerPosition({ lat, lng });
            map?.panTo({ lat, lng });
            setValidationError("");
            fetchAddressFromLatLng(lat, lng).then((address) => {
              const validation = validateAddress(address);
              if (validation.isValid) {
                setAddressDetails(address);
                setValidationError("");
              } else {
                setValidationError(validation.error);
                setAddressDetails(null);
              }
            });
          } else {
            setValidationError("Aapka current location Pakistan ke bahar hai. Please Pakistan ki koi location search karein.");
            setAddressDetails(null);
          }
        },
        (error) => {
          console.log("Location permission denied:", error);
          setValidationError("Location access denied. Please search for a Pakistan location manually.");
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      console.log("Geolocation is not supported by this browser.");
      setValidationError("Geolocation is not supported by your browser. Please search for a Pakistan address manually.");
    }
  }, [map]);

  const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      
      // Check if dragged location is within Pakistan bounds
      if (isWithinPakistanBounds(lat, lng)) {
        setMarkerPosition({ lat, lng });
        setValidationError("");
        const address = await fetchAddressFromLatLng(lat, lng);
        const validation = validateAddress(address);
        if (validation.isValid) {
          setAddressDetails(address);
          setValidationError("");
        } else {
          setValidationError(validation.error);
          setAddressDetails(null);
        }
      } else {
        setValidationError("Sirf Pakistan ki location select kar sakte hain. Please marker ko Pakistan ke andar move karein.");
        setAddressDetails(null);
      }
    }
  };

  const fetchAddressFromLatLng = async (
    lat: number,
    lng: number
  ): Promise<AddressDetails> => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
      );
      const data = await response.json();
      if (data.results.length) {
        const result = data.results[0];
        const addressComponents = result.address_components;
        const emirate =
          addressComponents.find((c: any) =>
            c.types.includes("administrative_area_level_1")
          )?.long_name || "";
        const city =
          addressComponents.find((c: any) => c.types.includes("locality"))
            ?.long_name || "";
        const streetAddress =
          addressComponents
            .filter(
              (c: any) =>
                c.types.includes("route") || c.types.includes("street_number")
            )
            .map((c: any) => c.long_name)
            .join(", ") || "";
        const country =
          addressComponents.find((c: any) => c.types.includes("country"))
            ?.long_name || "";
        const fullAddress = result.formatted_address;
        return {
          emirate,
          city,
          streetAddress,
          country,
          fullAddress,
          address: fullAddress,
        };
      }
      return {
        emirate: "",
        city: "",
        streetAddress: "",
        country: "",
        fullAddress: "",
        address: "",
      };
    } catch (error) {
      console.error("Error fetching address:", error);
      return {
        emirate: "",
        city: "",
        streetAddress: "",
        country: "",
        fullAddress: "",
        address: "",
      };
    }
  };

  const onPlaceChanged = () => {
    if (autocomplete.current) {
      const place = autocomplete.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location?.lat();
        const lng = place.geometry.location?.lng();

        if (lat && lng) {
          // Check if selected location is within Pakistan bounds
          if (isWithinPakistanBounds(lat, lng)) {
            setMarkerPosition({ lat, lng });
            map?.panTo({ lat, lng });
            setValidationError("");

            fetchAddressFromLatLng(lat, lng).then((address) => {
              const validation = validateAddress(address);
              if (validation.isValid) {
                setAddressDetails(address);
                setValidationError("");
              } else {
                setValidationError(validation.error);
                setAddressDetails(null);
              }
            });
          } else {
            setValidationError("Sirf Pakistan ki location select kar sakte hain. Please Pakistan ki koi location search karein.");
            setAddressDetails(null);
          }
        }
      } else {
        setValidationError("Invalid location selected. Please choose a valid address.");
        setAddressDetails(null);
      }
    } else {
      console.log("Autocomplete is not loaded yet!");
      setValidationError("Search is not ready yet. Please wait a moment.");
    }
  };

  const handleConfirm = () => {
    if (addressDetails) {
      const validation = validateAddress(addressDetails);
      if (validation.isValid) {
        setValidationError("");
        onConfirm(addressDetails);
      } else {
        setValidationError(validation.error);
      }
    } else {
      setValidationError("Please select a valid Pakistan location first.");
    }
  };

  const onLoadAutocomplete = (autocompleteInstance: google.maps.places.Autocomplete) => {
    autocomplete.current = autocompleteInstance;
    
    // Restrict autocomplete to Pakistan only
    if (autocompleteInstance) {
      autocompleteInstance.setComponentRestrictions({
        country: 'pk' // Pakistan country code
      });
      
      // Set bounds to Pakistan
      const pakistanBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(PAKISTAN_BOUNDS.south, PAKISTAN_BOUNDS.west),
        new google.maps.LatLng(PAKISTAN_BOUNDS.north, PAKISTAN_BOUNDS.east)
      );
      autocompleteInstance.setBounds(pakistanBounds);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`w-full max-h-[50vh] overflow-y-auto ${mapContainerClass}`}
      >
        <div className="relative">
          {isLoaded && (
            <Autocomplete
              onLoad={onLoadAutocomplete}
              onPlaceChanged={onPlaceChanged}
              className="absolute top-[10px] left-[10px] w-[calc(100%-20px)] z-10"
            >
              <div className="address-input-and-map-inner">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="font-size-18px text-[#707070]"
                />
                <input
                  id={inputId || "google-map-input"}
                  ref={inputRef}
                  type="text"
                  placeholder="Search Location"
                  className="text-[#2D2D2D] font-size-15px font-Poppins-Regular"
                />
              </div>
            </Autocomplete>
          )}
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{
                ...defaultContainerStyle,
                ...containerStyle,
              }}
              center={markerPosition}
              zoom={14}
              onLoad={(mapInstance) => setMap(mapInstance)}
              onUnmount={() => setMap(null)}
            >
              <Marker
                position={markerPosition}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            </GoogleMap>
          ) : (
            <div className="flex justify-center items-center h-[35vh]">
              <CircularProgress />
            </div>
          )}
          {addressDetails && !validationError && (
            <div className={`absolute bottom-[10px] left-[10px] px-3 py-2 w-[calc(100%-100px)] rounded-lg bg-white z-10 flex items-center ${addressDetailsClass}`}>
              <FontAwesomeIcon
                icon={faLocationDot}
                className="text-[#707070] font-size-26px mr-2"
              />
              <div className="">
                <span className="font-size-18px theme-color-green font-Poppins-SemiBold">
                  {`${addressDetails.emirate || ""}, ${
                    addressDetails.city || ""
                  }`}
                </span>
                <p className="font-size-15px text-[#707070] font-Poppins-Medium line-clamp-2">
                  {addressDetails.streetAddress || addressDetails.fullAddress}
                </p>
              </div>
            </div>
          )}
          
          {validationError && (
            <div className={`absolute bottom-[10px] left-[10px] px-3 py-2 w-[calc(100%-20px)] rounded-lg bg-red-50 border border-red-200 z-10 ${addressDetailsClass}`}>
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="text-red-500 font-size-20px mr-2"
                />
                <div className="">
                  <p className="font-size-15px text-red-600 font-Poppins-Medium">
                    {validationError}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={`w-full res-padd-top ${btnContainerClass}`}>
        <GradientBtn
          id={confirmBtnId}
          loader={loading}
          extraClass="w-full"
          ButtonText="Confirm Location"
          onPress={() => {
            if (addressDetails && !loading && !validationError) {
              handleConfirm();
            }
          }}
          fontsizeBtn="font-size-18px"
          fontfamilyBtn="font-Lexend-Medium"
          disabled={!addressDetails || !!validationError || loading}
        />
      </div>
    </div>
  );
};

export default GoogleMapComponent;